// Dice fold (pure-view binding): the board's dice are a function of
// mirrorState.dice. A die the mirror carries with no mesh spawns; a mesh the
// mirror no longer knows is disposed; a die laid somewhere new glides there;
// a bumped rollSeq tumbles it onto the server-drawn face. A Full lays every
// die down as it is, without replaying rolls that settled long ago.
//
// Where a die lies is the mirror's `x, z` and, on a card, `on: { cardId, u,
// w }` in the card's frame. An attached die is drawn from its card, so the
// fold never has to follow a card's moves: a card dragged with six dice on
// it changes nothing here.
import { addTween } from '../../../utils/game/animations';
import { adoptTrayGhost } from './diceTrayDrag';
import { moveDiceIntent } from '../../../utils/game/net/diceIntents';
import {
  animateDiceRoll, createDiceMesh, dieSpot, disposeDiceMesh, liftDie, placeDie, reskinDiceMesh, turnDieTo,
} from '../../../utils/game/diceMesh';
import { msg } from '../../../utils/game/logMessage';

/** World units of drift below which a mirror position is the mesh's own. */
const SAME_SPOT = 0.05;
const GLIDE_MS = 200;

export function applyDiceFromMirror(host, mirrorState, { isFull = false } = {}) {
  const dice = mirrorState?.dice;
  if (!dice || typeof dice !== 'object') return;
  const seen = (host._diceRollSeq ??= new Map());
  const list = host.state.gameState.dice;

  for (const [id, mesh] of host.diceMeshes) {
    if (dice[id]) continue;
    disposeDiceMesh(mesh);
    host.diceMeshes.delete(id);
    seen.delete(id);
    if (host.hoveredMesh === mesh) host.hoveredMesh = null;
  }
  const kept = list.filter((d) => dice[d.id]);
  if (kept.length !== list.length) host.state.gameState.dice = kept;

  for (const die of Object.values(dice)) {
    const mesh = host.diceMeshes.get(die.id);
    if (!mesh) {
      addDieMesh(host, die, isFull);
      seen.set(die.id, die.rollSeq);
      continue;
    }
    const instance = mesh.userData.diceInstance;
    instance.seat = die.seat;
    // The die wears the palette the server stamped at its spawn, whatever
    // either seat picks later.
    if ((instance.palette ?? null) !== (die.palette ?? null)) reskinDiceMesh(mesh, die.palette ?? null);
    if (placementDiffers(instance, die)) {
      instance.x = die.x;
      instance.z = die.z;
      instance.on = attachmentOf(die);
      // A die this pointer holds lands where it is dropped; the drop reports.
      if (host.dragging?.mesh !== mesh) glide(host, mesh);
    }

    const lastSeq = seen.get(die.id);
    seen.set(die.id, die.rollSeq);
    if (lastSeq !== undefined && die.rollSeq !== lastSeq && !isFull) {
      animateDiceRoll(mesh, die.value, host._diceContext());
      host.addLogEntry(
        msg('Rolled {die}: {value}', { die: die.dieType, value: die.value }),

        die.seat === host._localSeat() ? 'you' : 'opponent',
        'dice',
      );
    } else if (instance.value !== die.value) {
      // Set by hand, not rolled: tip it over onto the face, no tumble.
      turnDieTo(mesh, die.value, host._diceContext());
    }
  }
}

const attachmentOf = (die) => (die.on ? { cardId: die.on.cardId, u: die.on.u, w: die.on.w } : null);

function sameAttachment(a, b) {
  if (!a || !b) return !a && !b;
  return a.cardId === b.cardId && Math.abs(a.u - b.u) < SAME_SPOT && Math.abs(a.w - b.w) < SAME_SPOT;
}

// On a card, the card's frame is the place; the mirror's x, z is only the
// spot the die was dropped at, and the die has ridden on since.
function placementDiffers(instance, die) {
  if (!sameAttachment(instance.on, die.on)) return true;
  if (die.on) return false;
  return Math.abs(instance.x - die.x) > SAME_SPOT || Math.abs(instance.z - die.z) > SAME_SPOT;
}

function addDieMesh(host, die, isFull) {
  const instance = {
    id: die.id, dieType: die.dieType, x: die.x, z: die.z, value: die.value, seat: die.seat,
    on: attachmentOf(die), palette: die.palette ?? null,
  };
  host.state.gameState.dice.push(instance);
  // The die this seat just dropped out of the tray is already lying there,
  // settled by the drop, so it only needs the server's instance. It was
  // dropped in this seat's palette; if the server stamped another (the
  // announcement had not landed), it changes coat on the spot.
  const adopted = adoptTrayGhost(host, die);
  if (adopted) {
    const wore = adopted.userData.diceInstance?.palette ?? null;
    adopted.userData.diceInstance = instance;
    if (wore !== instance.palette) reskinDiceMesh(adopted, instance.palette);
    host.diceMeshes.set(die.id, adopted);
    return;
  }
  const mesh = createDiceMesh(instance);
  host.scene.scene.add(mesh);
  host.diceMeshes.set(die.id, mesh);
  // A die let go over the table drops onto it; a Full lays it down as it was.
  if (!isFull) liftDie(mesh);
  placeDie(mesh, host._diceContext());
}

// The other seat laid the die somewhere else: lift it, carry it over, put it
// down. A die already lying there (this seat's own drop, echoed) is only
// re-seated.
function glide(host, mesh) {
  const ctx = host._diceContext();
  const to = dieSpot(mesh, ctx);
  if (Math.abs(to.x - mesh.position.x) < SAME_SPOT && Math.abs(to.z - mesh.position.z) < SAME_SPOT) {
    placeDie(mesh, ctx);
    return;
  }
  liftDie(mesh);
  addTween({ target: mesh.position, property: 'x', from: mesh.position.x, to: to.x, duration: GLIDE_MS });
  addTween({
    target: mesh.position, property: 'z', from: mesh.position.z, to: to.z, duration: GLIDE_MS,
    onComplete: () => { if (host.diceMeshes.get(mesh.userData.diceInstance.id) === mesh) placeDie(mesh, host._diceContext()); },
  });
}

/**
 * The card a die was attached to has left the table. The die stays where it
 * lay; the seat that spawned it tells the server so, once, so the mirror (and
 * a reload, and the other seat) keep it there instead of on a card that is
 * gone. One reporter, so the two seats never race.
 */
export function reportOrphanedDie(host, mesh) {
  const inst = mesh.userData.diceInstance;
  if (inst.seat !== host._localSeat()) return;
  inst.on = null;
  host._sendMatchIntent(moveDiceIntent(inst.id, inst.x, inst.z, null));
}
